import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "./MapPage.module.css";
import { SplashContext } from "../App";

// JSON 파일 임포트
import animalHospitalsData from "../_data/animalHospitals.json";
import cafesData from "../_data/cafes.json";
import petCafesData from "../_data/petCafes.json";
import petFuneralHomesData from "../_data/petFuneralHomes.json";
import petParksData from "../_data/petParks.json";
import restaurantsData from "../_data/restaurants.json";

// Firestore 임포트 제거
// import { db } from "../firebase/firebase";
// import { doc, getDoc, collection } from "firebase/firestore";

interface PetLocation {
  id: string;
  name: string;
  lat: number;
  lng: number;
  image?: string;
  type: string;
  address?: string;
  description?: string;
  isMainMarker?: boolean; // 대표 마커 여부
  gridKey?: string; // 그리드 키
  displayPriority?: number; // 표시 우선순위
  distance?: string; // 현재 위치로부터의 거리
  thumbnail?: string; // 썸네일 이미지 URL (성능 최적화)
}

// Firestore에서 가져올 상세 정보 인터페이스
interface StoreDetailInfo {
  id: string;
  name: string;
  category?: string;
  phone?: string | null;
  address?: string;
  roadAddress?: string; // road_address 대신 roadAddress로 camelCase 사용
  detailInfo?: string; // 상세 설명
  hasImage?: boolean;
  imageUrls?: string[]; // 이미지 URL 배열
  sites?: string[]; // 웹사이트/SNS URL 배열
  hasWebsite?: boolean;
  markerDisplayInfo?: {
    brief: string;
    name: string;
    mainImageUrl: string;
  };
  location?: {
    geopoint: {
      _latitude: number;
      _longitude: number;
    };
  };
  x?: number; // 경도
  y?: number; // 위도
  geohash?: string;
  g?: {
    geohash: string;
    geopoint: {
      _latitude: number;
      _longitude: number;
    };
  };
  updatedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
}

declare global {
  interface Window {
    naver: any;
  }
}

// 위치 데이터 유형 정의
const DATA_TYPES = {
  ANIMAL_HOSPITALS: "동물병원",
  RESTAURANTS: "식당",
  CAFES: "카페",
  PET_CAFES: "애견카페",
  PET_PARKS: "애견공원",
  PET_FUNERAL_HOMES: "장례식장",
};

// 줌 레벨별 그리드 셀 크기 (픽셀)
const GRID_CELL_SIZE = {
  HIGH_ZOOM: 200, // 높은 줌 레벨 (15 이상)
  MID_ZOOM: 100, // 중간 줌 레벨 (12-15)
  LOW_ZOOM: 50, // 낮은 줌 레벨 (12 미만)
};

// 줌 레벨별 카테고리당 표시할 마커 수 제한
const MARKERS_PER_CATEGORY = {
  ZOOM_14: 50, // 줌 레벨 14에서는 카테고리당 10개
  ZOOM_13: 40, // 줌 레벨 13에서는 카테고리당 7개
  ZOOM_12: 30, // 줌 레벨 12에서는 카테고리당 5개
  ZOOM_11: 20, // 줌 레벨 11에서는 카테고리당 3개
  ZOOM_10: 10, // 줌 레벨 10 이하는 카테고리당 2개
};

// 줌 레벨별 표시할 마커 총 개수 제한
const TOTAL_MARKERS_BY_ZOOM = {
  ZOOM_15_PLUS: Infinity, // 줌 레벨 15 이상은 제한 없음
  ZOOM_14: 1000, // 줌 레벨 14에서는 최대 100개
  ZOOM_13: 800, // 줌 레벨 13에서는 최대 70개
  ZOOM_12: 600, // 줌 레벨 12에서는 최대 50개
  ZOOM_11: 400, // 줌 레벨 11에서는 최대 30개
  ZOOM_10: 200, // 줌 레벨 10 이하는 최대 20개
};

// NaverMap 아이콘 인터페이스 정의 (타입 안전성 확보)
interface NaverMarkerIcon {
  content: string;
  anchor: any; // naver.maps.Point 타입
}

// 좌표를 지역 이름으로 변환하는 함수
const getRegionFromCoords = (lat: number, lng: number): string => {
  // 간단한 지역 분류 로직 (실제로는 더 정확한 지역 구분이 필요함)
  if (lat >= 37.4 && lat <= 37.7 && lng >= 126.8 && lng <= 127.2) return "서울";
  if (lat >= 37.2 && lat <= 38.2 && lng >= 126.6 && lng <= 127.8) return "경기";
  if (lat >= 37.2 && lat <= 37.8 && lng >= 126.2 && lng <= 126.8) return "인천";
  if (lat >= 35.0 && lat <= 35.4 && lng >= 128.8 && lng <= 129.3) return "부산";
  if (lat >= 35.8 && lat <= 36.0 && lng >= 128.5 && lng <= 128.8) return "대구";
  if (lat >= 35.1 && lat <= 35.3 && lng >= 126.7 && lng <= 127.0) return "광주";
  if (lat >= 36.2 && lat <= 36.5 && lng >= 127.2 && lng <= 127.5) return "대전";
  if (lat >= 35.4 && lat <= 35.7 && lng >= 129.1 && lng <= 129.4) return "울산";
  if (lat >= 37.7 && lat <= 38.5 && lng >= 127.5 && lng <= 129.0) return "강원";
  if (lat >= 36.5 && lat <= 37.2 && lng >= 127.2 && lng <= 128.3) return "충북";
  if (lat >= 36.0 && lat <= 37.0 && lng >= 126.5 && lng <= 127.2) return "충남";
  if (lat >= 35.5 && lat <= 36.0 && lng >= 126.5 && lng <= 127.5) return "전북";
  if (lat >= 34.4 && lat <= 35.5 && lng >= 126.0 && lng <= 127.5) return "전남";
  if (lat >= 35.5 && lat <= 37.0 && lng >= 128.2 && lng <= 129.5) return "경북";
  if (lat >= 34.5 && lat <= 35.5 && lng >= 127.5 && lng <= 129.0) return "경남";
  if (lat >= 33.0 && lat <= 34.0 && lng >= 126.0 && lng <= 127.0) return "제주";

  return "기타";
};

// 카테고리별 매핑 정보
const CATEGORY_MAPPING: { [key: string]: string[] } = {
  "내 피드": ["내 피드"],
  "펫 카페/운동장": ["애견카페", "애견공원"],
  식당: ["식당"],
  카페: ["카페"],
  병원: ["동물병원"],
  장례식장: ["장례식장"],
};

const MapPage: React.FC = () => {
  const { splashComplete } = useContext(SplashContext);
  const navigate = useNavigate();
  const location = useLocation();
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [naverLoaded, setNaverLoaded] = useState<boolean>(false);
  const [currentLocation, setCurrentLocation] = useState<{
    lat: number;
    lng: number;
  }>({
    lat: 37.5665,
    lng: 126.978,
  });
  const [locationLoaded, setLocationLoaded] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [petLocations, setPetLocations] = useState<PetLocation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(15);
  const [selectedCategory, setSelectedCategory] = useState<string>("전체");

  // 핀 클릭 시 상세 정보 표시 관련 상태 추가
  const [selectedStore, setSelectedStore] = useState<PetLocation | null>(null);
  const [showStoreDetail, setShowStoreDetail] = useState<boolean>(false);
  const [storeDetailInfo, setStoreDetailInfo] =
    useState<StoreDetailInfo | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  // 매장까지의 거리를 저장할 상태 추가
  const [storeDistance, setStoreDistance] = useState<string>("");
  // 상세 정보 콘텐츠 표시 애니메이션을 위한 상태
  const [contentVisible, setContentVisible] = useState<boolean>(false);

  // 마커 관리 방식 변경: useState 대신 useRef 사용
  const markersRef = useRef<{ [key: string]: any }>({});
  const processingRef = useRef<boolean>(false);
  const renderingRequestedRef = useRef<boolean>(false);
  const [forceUpdate, setForceUpdate] = useState<number>(0);
  const currentBoundsRef = useRef<any>(null); // 현재 지도 경계 저장용

  // 마운트 상태 추적 (컴포넌트 언마운트 후 상태 업데이트 시도 방지)
  const mountedRef = useRef<boolean>(true);

  // 카테고리 스크롤 ref 추가
  const categoryScrollRef = useRef<HTMLDivElement>(null);

  // 모든 위치 데이터를 메모리에 저장 (초기에 한 번만 로드)
  const allLocationsRef = useRef<{
    hospitals: PetLocation[];
    cafes: PetLocation[];
    petCafes: PetLocation[];
    petParks: PetLocation[];
    funeralHomes: PetLocation[];
    restaurants: PetLocation[];
  }>({
    hospitals: [],
    cafes: [],
    petCafes: [],
    petParks: [],
    funeralHomes: [],
    restaurants: [],
  });

  // 공간 인덱싱을 위한 격자 사이즈 (도 단위)
  const SPATIAL_GRID_SIZE = 0.01; // 약 1km

  // 공간 인덱스 저장소
  const spatialIndexRef = useRef<{
    [key: string]: {
      // 그리드 키
      hospitals: PetLocation[];
      cafes: PetLocation[];
      petCafes: PetLocation[];
      petParks: PetLocation[];
      funeralHomes: PetLocation[];
      restaurants: PetLocation[];
    };
  }>({});

  // 좌표를 공간 인덱스 키로 변환
  const getSpatialGridKey = (lat: number, lng: number): string => {
    const gridX = Math.floor(lng / SPATIAL_GRID_SIZE);
    const gridY = Math.floor(lat / SPATIAL_GRID_SIZE);
    return `${gridX}:${gridY}`;
  };

  // 공간 인덱스 생성
  const createSpatialIndex = () => {
    console.log("공간 인덱스 생성 시작...");
    const startTime = performance.now();

    // 인덱스 객체 초기화
    spatialIndexRef.current = {};

    // 각 유형별 위치 데이터에 대해 인덱싱
    Object.entries(allLocationsRef.current).forEach(([type, locations]) => {
      // 데이터가 없으면 건너뜀
      if (!locations || locations.length === 0) {
        console.warn(`${type} 데이터가 비어있어 인덱싱 건너뜀`);
        return;
      }

      console.log(`${type} 인덱싱 시작 (${locations.length}개 항목)`);

      locations.forEach((location) => {
        const gridKey = getSpatialGridKey(location.lat, location.lng);

        // 해당 그리드 키가 없으면 생성
        if (!spatialIndexRef.current[gridKey]) {
          spatialIndexRef.current[gridKey] = {
            hospitals: [],
            cafes: [],
            petCafes: [],
            petParks: [],
            funeralHomes: [],
            restaurants: [],
          };
        }

        // 위치 타입에 따라 맞는 배열에 추가
        const locationType = getTypeKey(location.type);
        if (locationType && spatialIndexRef.current[gridKey][locationType]) {
          spatialIndexRef.current[gridKey][locationType].push(location);
        }
      });
    });

    const endTime = performance.now();
    console.log(
      `공간 인덱스 생성 완료: ${
        Object.keys(spatialIndexRef.current).length
      }개 그리드, 소요시간: ${(endTime - startTime).toFixed(2)}ms`
    );
  };

  // 위치 타입을 인덱스 키로 변환
  const getTypeKey = (
    type: string
  ): keyof typeof allLocationsRef.current | null => {
    switch (type) {
      case "동물병원":
        return "hospitals";
      case "카페":
        return "cafes";
      case "애견카페":
        return "petCafes";
      case "애견공원":
        return "petParks";
      case "장례식장":
        return "funeralHomes";
      case "식당":
        return "restaurants";
      default:
        return null;
    }
  };

  // 지도 영역 내에 있는 그리드 키 목록 가져오기
  const getGridKeysInBounds = (
    minLat: number,
    maxLat: number,
    minLng: number,
    maxLng: number
  ): string[] => {
    const startGridX = Math.floor(minLng / SPATIAL_GRID_SIZE);
    const endGridX = Math.ceil(maxLng / SPATIAL_GRID_SIZE);
    const startGridY = Math.floor(minLat / SPATIAL_GRID_SIZE);
    const endGridY = Math.ceil(maxLat / SPATIAL_GRID_SIZE);

    const gridKeys: string[] = [];

    for (let x = startGridX; x <= endGridX; x++) {
      for (let y = startGridY; y <= endGridY; y++) {
        gridKeys.push(`${x}:${y}`);
      }
    }

    return gridKeys;
  };

  // JSON 데이터 초기 로드 (컴포넌트 마운트 시 한 번만)
  useEffect(() => {
    console.log("JSON 데이터 로드 시도...");

    // 위치 데이터 변환 함수 (JSON → PetLocation)
    const convertLocationData = (data: any[], type: string): PetLocation[] => {
      if (!data || !Array.isArray(data) || data.length === 0) {
        console.warn(`${type} 데이터가 비어있거나 배열이 아님`);
        return [];
      }

      return data
        .map((item) => {
          // x, y 좌표가 없는 경우 건너뜀
          if (typeof item.x === "undefined" || typeof item.y === "undefined") {
            console.warn(`좌표 없는 항목 건너뜀:`, item);
            return null;
          }

          // 숫자 형식 확인 및 변환
          const x = typeof item.x === "string" ? parseFloat(item.x) : item.x;
          const y = typeof item.y === "string" ? parseFloat(item.y) : item.y;

          // 숫자가 아니거나 유효하지 않은 경우 건너뜀
          if (isNaN(x) || isNaN(y)) {
            console.warn(`유효하지 않은 좌표:`, item.x, item.y);
            return null;
          }

          return {
            id:
              item.id ||
              `${type}-${Math.random().toString(36).substring(2, 9)}`,
            name: item.name || "이름 없음",
            lat: y, // JSON에서는 y가 위도
            lng: x, // JSON에서는 x가 경도
            type: type,
            address: item.address || "",
            isMainMarker: false,
            displayPriority: 2,
            thumbnail: item.thumbnail,
          };
        })
        .filter(Boolean) as PetLocation[]; // null 항목 제거
    };

    try {
      // 모든 위치 데이터 메모리 로드
      allLocationsRef.current = {
        hospitals: convertLocationData(animalHospitalsData, "동물병원"),
        cafes: convertLocationData(cafesData as any[], "카페"),
        petCafes: convertLocationData(petCafesData, "애견카페"),
        petParks: convertLocationData(petParksData, "애견공원"),
        funeralHomes: convertLocationData(petFuneralHomesData, "장례식장"),
        restaurants: convertLocationData(restaurantsData as any[], "식당"),
      };

      // 상세 로그 출력
      Object.entries(allLocationsRef.current).forEach(([type, locations]) => {
        console.log(`${type} 데이터: ${locations.length}개 항목 로드됨`);
      });

      // 공간 인덱스 생성
      createSpatialIndex();
    } catch (error) {
      console.error("JSON 데이터 로드 오류:", error);
    }
  }, []);

  // 두 지점 간의 거리를 계산하는 헬퍼 함수 (Haversine 공식 사용)
  const getDistanceFromLatLonInKm = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 6371; // 지구 반경 (km)
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // 킬로미터 단위 거리
    return distance;
  };

  // 각도를 라디안으로 변환
  const deg2rad = (deg: number) => {
    return deg * (Math.PI / 180);
  };

  // 거리를 사용자 친화적인 형식으로 변환하는 함수
  const formatDistance = (distance: number): string => {
    try {
      if (distance < 1) {
        // 1km 미만은 m 단위로 표시 (소수점 없음)
        return `${Math.round(distance * 1000)}m`;
      } else if (distance < 10) {
        // 10km 미만은 소수점 한 자리까지 표시
        return `${distance.toFixed(1)}km`;
      } else {
        // 10km 이상은 정수로 표시
        return `${Math.round(distance)}km`;
      }
    } catch (error) {
      console.error("거리 포맷팅 중 오류:", error);
      return "거리 정보 없음";
    }
  };

  // 좌표와 거리 정보를 함께 계산하는 함수
  const calculateDistanceFromCurrentLocation = (
    targetLat: number,
    targetLng: number,
    currentLat: number,
    currentLng: number
  ): string => {
    try {
      const distanceInKm = getDistanceFromLatLonInKm(
        currentLat,
        currentLng,
        targetLat,
        targetLng
      );
      return formatDistance(distanceInKm);
    } catch (error) {
      console.error("거리 계산 중 오류:", error);
      return "거리 정보 없음";
    }
  };

  // 위경도 좌표를 픽셀 좌표로 변환
  const latLngToPixel = (lat: number, lng: number, mapInstance: any) => {
    if (!mapInstance) return null;
    const projection = mapInstance.getProjection();
    const point = new window.naver.maps.LatLng(lat, lng);
    return projection.fromCoordToOffset(point);
  };

  // 줌 레벨에 따른 그리드 셀 크기 결정
  const getGridCellSize = (zoom: number) => {
    if (zoom >= 15) return GRID_CELL_SIZE.HIGH_ZOOM;
    if (zoom >= 12) return GRID_CELL_SIZE.MID_ZOOM;
    return GRID_CELL_SIZE.LOW_ZOOM;
  };

  // 좌표를 그리드 셀로 변환
  const getGridKey = (lat: number, lng: number, mapInstance: any) => {
    const pixel = latLngToPixel(lat, lng, mapInstance);
    if (!pixel) return "";

    const zoom = mapInstance.getZoom();
    const cellSize = getGridCellSize(zoom);

    // 픽셀 좌표를 그리드 셀 인덱스로 변환
    const gridX = Math.floor(pixel.x / cellSize);
    const gridY = Math.floor(pixel.y / cellSize);

    return `${gridX}:${gridY}`;
  };

  // 위치가 현재 지도 영역 내에 있는지 확인
  const isLocationInBounds = (lat: number, lng: number, bounds: any) => {
    if (!bounds) return false;

    try {
      const position = new window.naver.maps.LatLng(lat, lng);
      return bounds.hasLatLng(position);
    } catch (error) {
      console.error("위치 영역 확인 중 오류:", error, lat, lng);
      return false;
    }
  };

  // 카테고리별 아이콘 및 색상 설정 함수 (전역으로 이동하여 재사용성 높임)
  const getCategoryStyle = (type: string) => {
    switch (type) {
      case "동물병원":
        return {
          icon: "/images/icons/map-hospital.svg",
          color: "#38C43C", // 녹색
        };
      case "카페":
        return {
          icon: "/images/icons/map-cafe.svg",
          color: "#019FF3", // 파란색
        };
      case "애견카페":
        return {
          icon: "/images/icons/map-playground.svg",
          color: "#EC2765", // 분홍색
        };
      case "애견공원":
        return {
          icon: "/images/icons/map-playground.svg",
          color: "#EC2765", // 분홍색
        };
      case "장례식장":
        return {
          icon: "/images/icons/map-funeral.svg",
          color: "#4048DB", // 보라색
        };
      case "식당":
        return {
          icon: "/images/icons/map-restaurant.svg",
          color: "#FD9B24", // 주황색
        };
      default:
        return {
          icon: "/images/icons/map-hospital.svg",
          color: "#E74C3C", // 빨간색 (기본값)
        };
    }
  };

  // 카테고리 필터링 함수
  const filterLocationsByCategory = (
    category: string,
    locations: PetLocation[]
  ) => {
    if (category === "전체") {
      return locations;
    }

    const typesToShow = CATEGORY_MAPPING[category] || [];
    return locations.filter((location) => typesToShow.includes(location.type));
  };

  // 카테고리 선택 핸들러
  const handleCategorySelect = (category: string) => {
    console.log(`🔄 카테고리 선택 시작: ${selectedCategory} → ${category}`);

    if (selectedCategory === category) {
      console.log(`⚠️ 같은 카테고리 선택됨: ${category}, 처리 건너뜀`);
      return;
    }

    setSelectedCategory(category);
    console.log(`✅ selectedCategory 상태 업데이트됨: ${category}`);

    // 카테고리 변경 시 모든 마커 초기화
    clearAllMarkers();

    // 카테고리 변경 후 새로운 마커 로드 (새 카테고리를 직접 전달)
    if (map && !processingRef.current) {
      // 약간의 지연 후 마커 다시 로드 (UI 응답성 유지)
      setTimeout(() => {
        if (mountedRef.current) {
          console.log(`🔄 새 카테고리 '${category}'에 대한 마커 로드 시작`);
          loadMarkersInView(map, category); // 새 카테고리를 직접 전달
        }
      }, 50);
    }
  };

  // 모든 마커 제거 (안전하게)
  const clearAllMarkers = () => {
    console.log("모든 마커 제거 시작");

    try {
      // 현재 마커 객체 목록 가져오기
      const currentMarkers = markersRef.current;

      // 각 마커 객체 제거
      Object.keys(currentMarkers).forEach((key) => {
        try {
          if (currentMarkers[key]) {
            currentMarkers[key].setMap(null);
            delete currentMarkers[key];
          }
        } catch (error) {
          console.error(`마커 제거 중 오류 (ID: ${key}):`, error);
        }
      });

      // 마커 참조 객체 초기화
      markersRef.current = {};
      console.log("모든 마커 제거 완료");
    } catch (error) {
      console.error("마커 제거 과정에서 오류 발생:", error);
      // 오류 발생 시에도 마커 참조 초기화
      markersRef.current = {};
    }
  };

  // 지도 영역 밖의 마커만 제거
  const clearOutOfBoundsMarkers = (bounds: any) => {
    if (!bounds) return;

    const currentMarkers = markersRef.current;
    let removedCount = 0;

    Object.entries(currentMarkers).forEach(([id, marker]) => {
      try {
        // 선택된 매장 마커인 경우 제거하지 않음
        if (selectedStore && id === selectedStore.id) {
          console.log(`선택된 마커 유지: ${id}`);
          return; // 이 마커는 건너뜀
        }

        const position = marker.getPosition();
        if (!bounds.hasLatLng(position)) {
          marker.setMap(null);
          delete currentMarkers[id];
          removedCount++;
        }
      } catch (error) {
        console.error(`마커 제거 중 오류 (ID: ${id}):`, error);
        // 오류 발생 시 안전하게 제거 (단, 선택된 마커는 제외)
        if (currentMarkers[id] && (!selectedStore || id !== selectedStore.id)) {
          currentMarkers[id].setMap(null);
          delete currentMarkers[id];
          removedCount++;
        }
      }
    });

    if (removedCount > 0) {
      console.log(`지도 영역 밖 마커 ${removedCount}개 제거됨`);
    }
  };

  // 줌 레벨에 따른 카테고리별 마커 수 제한 가져오기
  const getMarkerLimitPerCategory = (zoom: number) => {
    if (zoom >= 15) return Infinity; // 줌 레벨 15 이상은 제한 없음
    if (zoom === 14) return MARKERS_PER_CATEGORY.ZOOM_14;
    if (zoom === 13) return MARKERS_PER_CATEGORY.ZOOM_13;
    if (zoom === 12) return MARKERS_PER_CATEGORY.ZOOM_12;
    if (zoom === 11) return MARKERS_PER_CATEGORY.ZOOM_11;
    return MARKERS_PER_CATEGORY.ZOOM_10; // 줌 레벨 10 이하
  };

  // 위치 데이터를 그리드 기반으로 처리하여 대표 마커와 주변 마커 구분
  const processMarkersByGrid = (locations: PetLocation[], mapInstance: any) => {
    if (!mapInstance || locations.length === 0) return [];

    console.log(`마커 그리드 처리 시작: ${locations.length}개 위치 데이터`);

    // 선택된 카테고리에 따라 필터링
    let filteredLocations = filterLocationsByCategory(
      selectedCategory,
      locations
    );

    // 지도 영역 확인
    const bounds = mapInstance.getBounds();
    if (!bounds) return [];

    // 현재 지도 경계 저장
    currentBoundsRef.current = bounds;

    // 지도 영역 내 마커만 처리
    const markersInBounds = filteredLocations.filter((marker) => {
      return isLocationInBounds(marker.lat, marker.lng, bounds);
    });

    console.log(`지도 영역 내 마커: ${markersInBounds.length}개`);

    // 마커가 없으면 빈 배열 반환
    if (markersInBounds.length === 0) return [];

    // 각 마커에 현재 위치로부터의 거리 정보 추가
    markersInBounds.forEach((marker) => {
      // 이미 거리 정보가 있으면 건너뜀 (성능 최적화)
      if (!marker.distance) {
        marker.distance = calculateDistanceFromCurrentLocation(
          marker.lat,
          marker.lng,
          currentLocation.lat,
          currentLocation.lng
        );
      }
    });

    // 그리드 기반 마커 그룹화
    const gridMap: { [key: string]: PetLocation[] } = {};
    const displayMarkers: PetLocation[] = [];

    // 현재 줌 레벨 확인
    const zoom = mapInstance.getZoom();

    // 각 마커를 그리드에 할당
    markersInBounds.forEach((marker) => {
      const gridKey = getGridKey(marker.lat, marker.lng, mapInstance);
      marker.gridKey = gridKey;

      if (!gridMap[gridKey]) {
        gridMap[gridKey] = [];
      }

      gridMap[gridKey].push(marker);
    });

    // 줌 레벨에 따른 카테고리별 제한
    const markerLimitPerCategory = getMarkerLimitPerCategory(zoom);
    console.log(
      `줌 레벨 ${zoom}: 카테고리당 최대 ${markerLimitPerCategory}개 마커 표시`
    );

    // 카테고리별 표시 카운터
    const categoryCounters: { [category: string]: number } = {};

    // 카테고리별 마커 모음
    const markersPerCategory: { [category: string]: PetLocation[] } = {};

    // 일반 줌 레벨에서의 그리드 기반 마커 표시
    Object.values(gridMap).forEach((gridMarkers) => {
      if (gridMarkers.length === 0) return;

      // 그리드 내 마커 중 하나를 대표 마커로 설정
      const mainMarker = gridMarkers[0];
      mainMarker.isMainMarker = true;
      mainMarker.displayPriority = 1; // 높은 우선순위

      // 카테고리 카운터 초기화
      if (!categoryCounters[mainMarker.type]) {
        categoryCounters[mainMarker.type] = 0;
        markersPerCategory[mainMarker.type] = [];
      }

      // 대표 마커 추가 (카테고리 제한 적용)
      if (categoryCounters[mainMarker.type] < markerLimitPerCategory) {
        categoryCounters[mainMarker.type]++;
        markersPerCategory[mainMarker.type].push(mainMarker);
      }

      // 줌 레벨이 높을 때는 나머지 마커도 표시
      if (zoom >= 14) {
        for (let i = 1; i < gridMarkers.length; i++) {
          const smallMarker = gridMarkers[i];
          smallMarker.isMainMarker = false;
          smallMarker.displayPriority = 2; // 낮은 우선순위

          // 카테고리 카운터 초기화
          if (!categoryCounters[smallMarker.type]) {
            categoryCounters[smallMarker.type] = 0;
            markersPerCategory[smallMarker.type] = [];
          }

          // 작은 마커 추가 (카테고리 제한 적용)
          if (categoryCounters[smallMarker.type] < markerLimitPerCategory) {
            categoryCounters[smallMarker.type]++;
            markersPerCategory[smallMarker.type].push(smallMarker);
          }
        }
      }
    });

    // 카테고리별 마커를 합쳐서 최종 표시 목록 생성
    Object.values(markersPerCategory).forEach((markers) => {
      // 카테고리별로 마커를 랜덤하게 섞어서 선택
      if (
        markers.length > markerLimitPerCategory &&
        markerLimitPerCategory !== Infinity
      ) {
        // 마커 랜덤하게 섞기
        const shuffled = [...markers].sort(() => 0.5 - Math.random());
        // 제한된 개수만 선택
        displayMarkers.push(...shuffled.slice(0, markerLimitPerCategory));
      } else {
        displayMarkers.push(...markers);
      }
    });

    // 각 카테고리별 표시 마커 수 로깅
    Object.entries(categoryCounters).forEach(([category, count]) => {
      console.log(`카테고리 ${category}: ${count}개 마커 표시`);
    });

    // 우선순위에 따라 정렬 (대표 마커가 먼저 그려지도록)
    const sortedMarkers = displayMarkers.sort(
      (a, b) => (a.displayPriority || 2) - (b.displayPriority || 2)
    );

    console.log(`최종 표시할 마커: ${sortedMarkers.length}개`);
    return sortedMarkers;
  };

  // 줌 레벨에 따라 점 마커 생성 여부와 기준을 결정하는 함수
  const getDotMarkerCriteria = (
    zoom: number
  ): {
    useGridCriterion: boolean;
    gridThreshold: number;
    useMainMarkerOnly: boolean;
  } => {
    // 줌 레벨 18 이상: 모든 마커를 일반 마커로 표시
    if (zoom >= 18) {
      return {
        useGridCriterion: false,
        gridThreshold: 0,
        useMainMarkerOnly: false,
      };
    }

    // 줌 레벨 17: 거의 모든 마커를 일반 마커로, 극히 일부만 점 마커
    if (zoom >= 17) {
      return {
        useGridCriterion: true,
        gridThreshold: 5, // 그리드당 5개 이상일 때만 점 마커 사용
        useMainMarkerOnly: false,
      };
    }

    // 줌 레벨 16: 일반 마커와 점 마커 혼합
    if (zoom >= 16) {
      return {
        useGridCriterion: true,
        gridThreshold: 3, // 그리드당 3개 이상이면 점 마커 사용
        useMainMarkerOnly: false,
      };
    }

    // 줌 레벨 15: 점 마커 사용 시작
    if (zoom >= 15) {
      return {
        useGridCriterion: true,
        gridThreshold: 2, // 그리드당 2개 이상이면 점 마커 사용
        useMainMarkerOnly: false,
      };
    }

    // 줌 레벨 13-14: 대부분 점 마커, 일부 대표 마커만 일반 마커
    if (zoom >= 13) {
      return {
        useGridCriterion: true,
        gridThreshold: 1, // 그리드당 1개 이상이면 점 마커 사용
        useMainMarkerOnly: true, // 대표 마커만 일반 마커로 표시
      };
    }

    // 줌 레벨 12 이하: 거의 모든 마커를 점 마커로 표시
    return {
      useGridCriterion: true, // 그리드 조건 무시
      gridThreshold: 1,
      useMainMarkerOnly: true, // 대표 마커만 일반 마커로 표시
    };
  };

  // 마커를 지도에 표시
  const renderMarkers = (mapInstance: any) => {
    if (
      !mapInstance ||
      !mapInstance.getBounds ||
      processingRef.current ||
      !mountedRef.current
    ) {
      console.log("마커 렌더링 스킵: 처리 중이거나 지도가 준비되지 않음");
      if (mountedRef.current) renderingRequestedRef.current = true;
      return;
    }

    processingRef.current = true;
    console.log(`마커 렌더링 시작: 총 ${petLocations.length}개의 위치 데이터`);

    try {
      // 현재 지도 경계 가져오기
      const bounds = mapInstance.getBounds();
      if (!bounds) {
        console.log("지도 경계를 가져올 수 없어 렌더링 스킵");
        processingRef.current = false;
        return;
      }

      // 언마운트 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 현재 지도 경계 저장
      currentBoundsRef.current = bounds;

      // 지도 영역 밖의 마커만 제거 (효율적인 관리)
      clearOutOfBoundsMarkers(bounds);

      // 언마운트 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 표시할 마커 목록 생성 (그리드 기반)
      let processedLocations = processMarkersByGrid(petLocations, mapInstance);

      if (processedLocations.length === 0) {
        console.log("표시할 마커가 없음");
        processingRef.current = false;
        return;
      }

      // 언마운트 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 그리드 키별 마커 수 추적 (점 마커 처리용)
      const gridMarkerCounts: { [key: string]: number } = {};

      // 현재 줌 레벨
      const zoom = mapInstance.getZoom();

      // 먼저 그리드별 마커 수 집계
      processedLocations.forEach((location) => {
        if (location.gridKey) {
          if (!gridMarkerCounts[location.gridKey]) {
            gridMarkerCounts[location.gridKey] = 0;
          }
          gridMarkerCounts[location.gridKey]++;
        }
      });

      // 언마운트 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 마커 생성 및 표시
      for (let i = 0; i < processedLocations.length; i++) {
        // 50개 마커마다 언마운트 상태 확인 (성능 최적화)
        if (i % 50 === 0 && !mountedRef.current) {
          console.log("마커 처리 중 언마운트 감지, 처리 중단");
          processingRef.current = false;
          return;
        }

        const location = processedLocations[i];
        try {
          // 좌표를 기반으로 지역 결정
          const region = getRegionFromCoords(location.lat, location.lng);

          // 마커 카테고리 스타일 정보 가져오기
          const categoryStyle = getCategoryStyle(location.type);

          // 점 마커 사용 여부 결정 (줌 레벨에 따른 동적 조정)
          const dotCriteria = getDotMarkerCriteria(zoom);
          const isGridCrowded =
            location.gridKey &&
            (dotCriteria.useGridCriterion
              ? gridMarkerCounts[location.gridKey] > dotCriteria.gridThreshold
              : false);

          const shouldUseDotStyle =
            (dotCriteria.useMainMarkerOnly ? !location.isMainMarker : true) &&
            isGridCrowded;

          // 기존 마커가 있으면 업데이트
          if (markersRef.current[location.id]) {
            updateExistingMarker(
              location,
              mapInstance,
              region as string,
              categoryStyle,
              shouldUseDotStyle as boolean
            );
            continue; // 업데이트 완료 후 다음 마커로
          }

          // 새 마커 생성
          createNewMarker(
            location,
            mapInstance,
            region as string,
            categoryStyle,
            shouldUseDotStyle as boolean
          );
        } catch (error) {
          console.error(`마커 처리 오류 (ID: ${location.id}):`, error);
        }
      }

      // 언마운트 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      console.log(
        `마커 렌더링 완료: ${
          Object.keys(markersRef.current).length
        }개 마커 표시됨`
      );
    } catch (error) {
      console.error("마커 렌더링 오류:", error);
    } finally {
      processingRef.current = false;

      // 언마운트 상태에서는 추가 렌더링 요청 무시
      if (renderingRequestedRef.current && mountedRef.current) {
        renderingRequestedRef.current = false;
        setTimeout(() => renderMarkers(mapInstance), 100);
      } else if (renderingRequestedRef.current) {
        renderingRequestedRef.current = false;
      }
    }
  };

  // 기존 마커 업데이트 (별도 함수로 추출)
  const updateExistingMarker = (
    location: PetLocation,
    mapInstance: any,
    region: string,
    categoryStyle: { icon: string; color: string },
    shouldUseDotStyle: boolean
  ) => {
    try {
      const existingMarker = markersRef.current[location.id];
      existingMarker.setPosition(
        new window.naver.maps.LatLng(location.lat, location.lng)
      );

      // 선택된 마커인지 확인
      const isSelected =
        selectedStore !== null && selectedStore.id === location.id;

      // 마커 스타일 준비
      const markerIcon = getMarkerStyle(
        location,
        mapInstance,
        region,
        isSelected
      );

      // 마커 아이콘 업데이트
      existingMarker.setIcon(markerIcon);
    } catch (error) {
      console.error("마커 위치 업데이트 오류:", error);
      // 오류 발생 시 기존 마커 제거 후 새로 생성 로직
      if (markersRef.current[location.id]) {
        markersRef.current[location.id].setMap(null);
        delete markersRef.current[location.id];
      }
      // 새 마커 생성은 호출 위치에서 자동으로 처리됨
    }
  };

  // 새 마커 생성 (별도 함수로 추출)
  const createNewMarker = (
    location: PetLocation,
    mapInstance: any,
    region: string,
    categoryStyle: { icon: string; color: string },
    shouldUseDotStyle: boolean
  ) => {
    // 선택된 마커인지 확인
    const isSelected =
      selectedStore !== null && selectedStore.id === location.id;

    // 마커 스타일 준비
    const markerIcon = getMarkerStyle(
      location,
      mapInstance,
      region,
      isSelected
    );

    // 새 마커 생성
    const marker = new window.naver.maps.Marker({
      position: new window.naver.maps.LatLng(location.lat, location.lng),
      map: mapInstance,
      icon: markerIcon,
      title: location.name,
      clickable: true,
    });

    // 마커 클릭 이벤트 핸들러
    window.naver.maps.Event.addListener(marker, "click", (e: any) => {
      // 이벤트 버블링 방지
      if (e && e.domEvent) {
        e.domEvent.stopPropagation();
      }

      // 마커 클릭 시 매장 상세 정보 표시
      console.log("마커 클릭:", location);
      handleStoreSelect(location);
    });

    // 생성된 마커 저장
    markersRef.current[location.id] = marker;
  };

  // 마커 스타일 결정 (지역 정보 추가)
  const getMarkerStyle = (
    location: PetLocation,
    mapInstance: any,
    region?: string,
    isSelected?: boolean
  ): NaverMarkerIcon => {
    if (!mapInstance)
      return {
        content: "",
        anchor: new window.naver.maps.Point(0, 0),
      };

    // 지역 정보가 없으면 좌표로부터 지역 결정
    const markerRegion =
      region || getRegionFromCoords(location.lat, location.lng);

    // 카테고리 스타일 정보 가져오기
    const markerInfo = getCategoryStyle(location.type);

    // 선택된 마커이거나 대표 마커는 아이콘, 주변 마커는 작은 점으로 표시
    if (isSelected || location.isMainMarker) {
      const markerSize = isSelected ? 45 : 30; // 선택된 마커는 조금 더 크게
      const markerBorder = isSelected ? 3 : 0; // 선택된 마커는 조금 더 크게
      const iconSize = markerSize * 0.75;

      // 전역 클래스 사용 (CSS 모듈에서 :global 선택자로 정의)
      return {
        content: `<div class="pet-location-marker ${location.type}" 
                     data-region="${markerRegion}"
                     style="width: ${markerSize}px; height: ${markerSize}px; background-color: ${markerInfo.color}; border: ${markerBorder}px solid #fff; display: flex; align-items: center; justify-content: center; position: relative; cursor: pointer;">
               <img src="${markerInfo.icon}" width="${iconSize}" height="${iconSize}" alt="${location.type}" />
            </div>`,
        anchor: new window.naver.maps.Point(markerSize / 2, markerSize / 2),
      };
    } else {
      // 점 마커 크기 조정 (줌 레벨에 따라)
      const smallSize = 12;

      // 전역 클래스 사용 (CSS 모듈에서 :global 선택자로 정의)
      return {
        content: `<div class="pet-location-marker-small ${location.type}" 
                     data-region="${markerRegion}"
                     style="width: ${smallSize}px; height: ${smallSize}px; background-color: ${markerInfo.color}; cursor: pointer;">
            </div>`,
        anchor: new window.naver.maps.Point(smallSize / 2, smallSize / 2),
      };
    }
  };

  // 지도 영역 내에 있는 마커 로드
  const loadMarkersInView = async (
    mapInstance = map,
    targetCategory?: string
  ) => {
    // targetCategory가 제공되면 사용하고, 없으면 현재 selectedCategory 사용
    const categoryToUse = targetCategory || selectedCategory;

    console.log("loadMarkersInView 호출", mapInstance, mountedRef.current);
    console.log(
      `🎯 사용할 카테고리: ${categoryToUse} (전달받은: ${targetCategory}, 현재: ${selectedCategory})`
    );

    if (!mapInstance || !mountedRef.current) {
      console.log("loadMarkersInView 조기 종료: 맵 없음 또는 언마운트됨");
      return;
    }

    // 이미 처리 중이면 건너뜀
    if (processingRef.current) {
      console.log("이미 데이터 로드 중... 요청 무시");
      return;
    }

    processingRef.current = true;
    if (mountedRef.current) {
      console.log("로딩 상태 true로 설정");
      setIsLoading(true);
    } else {
      console.log("마운트 해제되어 로딩 상태 변경 안함");
      processingRef.current = false;
      return; // 언마운트된 경우 즉시 종료
    }

    try {
      // 현재 지도 경계 가져오기
      if (!mapInstance.getBounds) {
        console.error(
          "맵 인스턴스에 getBounds 메서드가 없습니다:",
          mapInstance
        );
        processingRef.current = false;
        if (mountedRef.current) setIsLoading(false);
        return;
      }

      const bounds = mapInstance.getBounds();
      if (!bounds) {
        console.error("맵 경계를 가져올 수 없습니다.");
        processingRef.current = false;
        if (mountedRef.current) setIsLoading(false);
        return;
      }

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 현재 지도 경계 저장
      currentBoundsRef.current = bounds;

      const sw = bounds.getSW();
      const ne = bounds.getNE();

      const startTime = performance.now();

      console.log(
        `지도 경계: SW(${sw.lng()}, ${sw.lat()}) - NE(${ne.lng()}, ${ne.lat()})`
      );

      // 현재 줌 레벨 저장
      const zoom = mapInstance.getZoom();
      if (mountedRef.current) setCurrentZoom(zoom);

      console.log(`현재 줌 레벨: ${zoom}`);

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 지도 영역 밖의 마커만 제거 (효율적인 관리)
      clearOutOfBoundsMarkers(bounds);

      // 지도 경계 내에 있는 데이터 필터링
      const minLat = sw.lat();
      const maxLat = ne.lat();
      const minLng = sw.lng();
      const maxLng = ne.lng();

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 공간 인덱스를 사용하여 관련 그리드 가져오기
      const relevantGridKeys = getGridKeysInBounds(
        minLat,
        maxLat,
        minLng,
        maxLng
      );
      console.log(`관련 그리드 ${relevantGridKeys.length}개 발견`);

      // 그리드 기반 빠른 검색
      const findLocationsInGrids = <
        T extends keyof typeof allLocationsRef.current
      >(
        type: T
      ): PetLocation[] => {
        const result: PetLocation[] = [];

        // 관련 그리드에서 위치 검색
        relevantGridKeys.forEach((gridKey) => {
          const gridData = spatialIndexRef.current[gridKey];
          if (gridData && gridData[type]) {
            // 최종 필터링 (그리드 경계와 실제 지도 영역의 차이 처리)
            gridData[type].forEach((location) => {
              if (
                location.lat >= minLat &&
                location.lat <= maxLat &&
                location.lng >= minLng &&
                location.lng <= maxLng
              ) {
                result.push(location);
              }
            });
          }
        });

        return result;
      };

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 데이터 로드 및 필터링 - 카테고리별 분기 처리
      let queryResults: PetLocation[] = [];

      console.log(`🏷️ 현재 selectedCategory: '${selectedCategory}'`);
      console.log(`🎯 실제 사용할 카테고리: '${categoryToUse}'`);

      // 선택된 카테고리에 따라 필요한 데이터만 로드
      if (categoryToUse === "전체") {
        console.log(`📁 전체 카테고리 - 모든 데이터 로드`);
        // 전체 카테고리 선택 시 모든 데이터 로드
        queryResults = [
          ...findLocationsInGrids("restaurants"),
          ...findLocationsInGrids("cafes"),
          ...findLocationsInGrids("petCafes"),
          ...findLocationsInGrids("hospitals"),
          ...findLocationsInGrids("petParks"),
          ...findLocationsInGrids("funeralHomes"),
        ];
      } else if (categoryToUse === "내 피드") {
        console.log(`📁 내 피드 카테고리 - 빈 데이터 반환`);
        // 내 피드는 현재 빈 배열 (추후 구현)
        queryResults = [];
      } else if (categoryToUse === "펫 카페/운동장") {
        console.log(
          `📁 펫 카페/운동장 카테고리 - 펫카페, 애견공원 데이터 로드`
        );
        // 펫 카페와 애견공원 데이터만 로드
        queryResults = [
          ...findLocationsInGrids("petCafes"),
          ...findLocationsInGrids("petParks"),
        ];
      } else if (categoryToUse === "식당") {
        console.log(`📁 식당 카테고리 - 식당 데이터만 로드`);
        // 식당 데이터만 로드
        queryResults = [...findLocationsInGrids("restaurants")];
      } else if (categoryToUse === "카페") {
        console.log(`📁 카페 카테고리 - 카페 데이터만 로드`);
        // 카페 데이터만 로드
        queryResults = [...findLocationsInGrids("cafes")];
      } else if (categoryToUse === "병원") {
        console.log(`📁 병원 카테고리 - 동물병원 데이터만 로드`);
        // 동물병원 데이터만 로드
        queryResults = [...findLocationsInGrids("hospitals")];
      } else if (categoryToUse === "장례식장") {
        console.log(`📁 장례식장 카테고리 - 장례식장 데이터만 로드`);
        // 장례식장 데이터만 로드
        queryResults = [...findLocationsInGrids("funeralHomes")];
      } else {
        // 알 수 없는 카테고리의 경우 전체 데이터 로드
        console.warn(
          `❌ 알 수 없는 카테고리: ${selectedCategory}, 전체 데이터 로드`
        );
        queryResults = [
          ...findLocationsInGrids("restaurants"),
          ...findLocationsInGrids("cafes"),
          ...findLocationsInGrids("petCafes"),
          ...findLocationsInGrids("hospitals"),
          ...findLocationsInGrids("petParks"),
          ...findLocationsInGrids("funeralHomes"),
        ];
      }

      console.log(
        `📊 카테고리 '${selectedCategory}'에 대한 쿼리 결과: ${queryResults.length}개`
      );

      // 선택된 매장이 있는 경우, 항상 결과에 포함시킴
      if (
        selectedStore &&
        !queryResults.some((location) => location.id === selectedStore.id)
      ) {
        console.log("⭐ 선택된 매장 마커 유지:", selectedStore.name);
        queryResults.push(selectedStore);
      }

      const endTime = performance.now();
      console.log(
        `위치 데이터 필터링 완료: ${(endTime - startTime).toFixed(2)}ms`
      );
      console.log(`총 쿼리 결과: ${queryResults.length}개 마커 로드됨`);

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 줌 레벨에 따른 표시 제한
      const totalMarkerLimit = getTotalMarkerLimit(zoom);
      console.log(
        `줌 레벨 ${zoom}에서 표시할 최대 마커 수: ${totalMarkerLimit}`
      );

      // 중복 제거
      const uniqueLocations = Array.from(
        new Map(queryResults.map((item) => [item.id, item])).values()
      );
      console.log(`중복 제거 후 마커 수: ${uniqueLocations.length}개`);

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 위치 기반 랜덤 선택 (줌 레벨에 따른 제한 적용)
      let selectedLocations = uniqueLocations;

      // 줌 레벨이 15 미만일 때만 마커 수 제한
      if (zoom < 15 && uniqueLocations.length > totalMarkerLimit) {
        console.log(
          `마커 수 제한 적용: ${uniqueLocations.length}개 → ${totalMarkerLimit}개`
        );

        // 모든 마커를 랜덤하게 섞기
        const shuffled = [...uniqueLocations].sort(() => 0.5 - Math.random());

        // 제한된 개수만 선택
        selectedLocations = shuffled.slice(0, totalMarkerLimit);

        // 카테고리별 선택된 마커 수 계산 (로깅용)
        const categoryCounts: { [category: string]: number } = {};
        selectedLocations.forEach((location) => {
          if (!categoryCounts[location.type]) {
            categoryCounts[location.type] = 0;
          }
          categoryCounts[location.type]++;
        });

        // 카테고리별 마커 수 로깅
        console.log("카테고리별 선택된 마커 수:");
        Object.entries(categoryCounts).forEach(([category, count]) => {
          console.log(`- ${category}: ${count}개`);
        });
      }

      // 마운트 상태 한번 더 확인
      if (!mountedRef.current) {
        processingRef.current = false;
        return;
      }

      // 최종 마커 위치 데이터 설정
      if (selectedLocations.length > 0 && mountedRef.current) {
        console.log(`${selectedLocations.length}개의 새 위치 로드 완료`);

        // 기존 데이터 중 지도 내 영역에 있는 데이터 필터링
        const existingInBounds = petLocations.filter((loc) =>
          isLocationInBounds(loc.lat, loc.lng, bounds)
        );

        // 기존 ID 추출
        const existingIds = new Set(existingInBounds.map((loc) => loc.id));

        // 중복 제거 (기존 데이터에 없는 새 데이터만 추가)
        const uniqueNewLocations = selectedLocations.filter(
          (loc) => !existingIds.has(loc.id)
        );

        console.log(
          `기존 데이터 중 지도 내 위치: ${existingInBounds.length}개, 추가되는 새 위치: ${uniqueNewLocations.length}개`
        );

        // 모든 위치 데이터 병합
        let allLocations = [...existingInBounds, ...uniqueNewLocations];

        // 마운트 상태 마지막 확인
        if (mountedRef.current) {
          setPetLocations(allLocations);
          setForceUpdate((prev) => prev + 1);
        }
      } else if (petLocations.length > 0 && mountedRef.current) {
        // 이미 로드된 데이터가 있으면, 지도 영역 내 데이터만 필터링
        console.log(
          "새 데이터 없음, 기존 데이터 중 지도 영역 내 데이터만 유지"
        );

        const locationsInBounds = petLocations.filter((loc) =>
          isLocationInBounds(loc.lat, loc.lng, bounds)
        );

        if (mountedRef.current) {
          setPetLocations(locationsInBounds);
          setForceUpdate((prev) => prev + 1);
        }
      } else if (mountedRef.current) {
        console.log("표시할 마커가 없음");
        setPetLocations([]);
        setForceUpdate((prev) => prev + 1);
      }
    } catch (error) {
      console.error("데이터 로드 오류:", error);
    } finally {
      processingRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  };

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    // 컴포넌트 마운트 시 명시적으로 마운트 상태 설정
    mountedRef.current = true;
    console.log("컴포넌트 마운트됨, mountedRef:", mountedRef.current);

    return () => {
      // 언마운트 상태 설정 (가장 먼저 실행)
      mountedRef.current = false;
      console.log("컴포넌트 언마운트됨, mountedRef:", mountedRef.current);

      // 모든 타이머 정리
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
        loadTimeoutRef.current = null;
      }

      // 스크롤 타이머 정리
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }

      // 진행 중인 프로세스 정리
      processingRef.current = false;
      renderingRequestedRef.current = false;

      // 컴포넌트 언마운트 시 모든 마커 제거
      try {
        clearAllMarkers();
      } catch (error) {
        console.error("언마운트 시 마커 제거 오류:", error);
      }
    };
  }, []);

  // 줌 레벨이나 위치 데이터 변경 시 마커 업데이트
  useEffect(() => {
    if (map) {
      const zoom = map.getZoom();
      console.log(
        `마커 갱신: 줌 레벨 ${zoom}, 데이터 ${petLocations.length}개, 강제 업데이트 ${forceUpdate}, 마운트 상태: ${mountedRef.current}`
      );

      // 이미 처리 중이면 건너뜀
      if (!processingRef.current && mountedRef.current) {
        renderMarkers(map);
      } else {
        renderingRequestedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petLocations, currentZoom, forceUpdate]);

  // 네이버 지도 초기화 함수
  const initializeNaverMap = React.useCallback(
    (location: { lat: number; lng: number }) => {
      if (!mapRef.current || !window.naver || !window.naver.maps) return;

      console.log("네이버 지도 초기화:", location);

      // 지도 옵션 설정
      const mapOptions = {
        center: new window.naver.maps.LatLng(location.lat, location.lng),
        zoom: 15,
        zoomControl: false,
        zoomControlOptions: {
          position: window.naver.maps.Position.TOP_RIGHT,
        },
        mapTypeControl: false,
        scaleControl: false,
        logoControl: false,
        mapDataControl: false,
      };

      // 지도 생성
      const newMap = new window.naver.maps.Map(mapRef.current, mapOptions);
      setMap(newMap);

      // 지도 클릭 이벤트 - 마커가 아닌 영역 클릭 시 선택 해제
      window.naver.maps.Event.addListener(newMap, "click", () => {
        console.log("지도 클릭: 선택된 마커 해제");

        // 마커 선택 상태가 있는 경우에만 처리
        if (mountedRef.current) {
          // 상세 정보 닫기 함수 호출
          handleCloseStoreDetail();
        }
      });

      // 지도 생성 후 마커 데이터 로드
      setCurrentZoom(newMap.getZoom());
      loadMarkersInView(newMap, selectedCategory);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [] // 의존성 배열에서 selectedStore 제거
  );

  // 스플래시 화면이 종료된 후 네이버 지도 API 로드
  useEffect(() => {
    if (!splashComplete) return;

    const loadNaverMapsScript = () => {
      if (document.getElementById("naver-maps-script")) {
        setScriptLoaded(true);
        return;
      }

      console.log("네이버 지도 스크립트 로딩 시작...");

      // 메인 지도 API 스크립트 로드
      const script = document.createElement("script");
      script.id = "naver-maps-script";
      script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=3ebgq7qsxr&submodules=geocoder`;
      script.async = true;
      script.onload = () => {
        console.log("네이버 지도 스크립트 로드 완료");
        setScriptLoaded(true);
      };
      script.onerror = (error) => {
        console.error("네이버 지도 스크립트 로드 오류:", error);
      };
      document.head.appendChild(script);
    };

    loadNaverMapsScript();

    return () => {
      const script = document.getElementById("naver-maps-script");
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [splashComplete]);

  // 네이버 맵 로딩 확인
  useEffect(() => {
    if (!scriptLoaded) return;

    const checkNaverMapsLoaded = () => {
      if (window.naver && window.naver.maps) {
        console.log("네이버 지도 API 준비 완료");
        setNaverLoaded(true);
      } else {
        setTimeout(checkNaverMapsLoaded, 100);
      }
    };

    checkNaverMapsLoaded();
  }, [scriptLoaded]);

  // 현재 위치 가져오기 및 지도 초기화
  useEffect(() => {
    if (!naverLoaded || !mapRef.current) return;

    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            console.log(
              "현재 위치 조회 성공:",
              newLocation,
              "마운트 상태:",
              mountedRef.current
            );
            setCurrentLocation(newLocation);
            setLocationLoaded(true);

            // 현재 위치 기반으로 지도 초기화
            if (mountedRef.current) {
              initializeNaverMap(newLocation);
            }
          },
          (error) => {
            console.error(
              "현재 위치 조회 오류:",
              error,
              "마운트 상태:",
              mountedRef.current
            );
            setLocationLoaded(true);

            // 위치 정보를 가져오지 못해도 기본 위치로 지도 초기화
            if (mountedRef.current) {
              initializeNaverMap(currentLocation);
            }
          },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
      } else {
        console.warn(
          "이 브라우저에서는 위치 정보를 지원하지 않습니다",
          "마운트 상태:",
          mountedRef.current
        );
        setLocationLoaded(true);

        // 위치 정보를 지원하지 않는 브라우저도 기본 위치로 지도 초기화
        if (mountedRef.current) {
          initializeNaverMap(currentLocation);
        }
      }
    };

    getUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naverLoaded]);

  // 지도 컨테이너 크기 조정 및 리사이즈 처리
  useEffect(() => {
    const handleResize = () => {
      if (map) {
        window.naver.maps.Event.trigger(map, "resize");
      }
    };

    const resizeTimer = setTimeout(() => {
      handleResize();
    }, 300);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, [map]);

  // 지도 초기화 시 마커 데이터 로드 (이벤트 연결)
  useEffect(() => {
    if (scriptLoaded && map) {
      // 지도 영역 변경 이벤트 (실제 데이터 로드는 이벤트 발생 시점에 수행)
      const handleBoundsChanged = () => {
        // 컴포넌트가 언마운트된 경우 처리 중단
        if (!mountedRef.current) {
          console.log("언마운트 상태: 바운드 변경 이벤트 무시");
          return;
        }

        // 스로틀링: 마지막 호출 후 일정 시간이 지나야 실행
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
        }

        // 300ms 지연 후 데이터 로드 수행
        loadTimeoutRef.current = setTimeout(() => {
          // 타임아웃 실행 시점에도 마운트 상태 재확인
          if (!mountedRef.current) {
            console.log("언마운트 상태: 지연된 로드 취소");
            return;
          }

          console.log(
            "로딩 상태:",
            isLoading,
            "처리 중:",
            processingRef.current,
            "마운트 상태:",
            mountedRef.current
          );

          // 로딩 상태가 아니고 처리 중이 아닐 때만 로드
          if (!isLoading && !processingRef.current && mountedRef.current) {
            loadMarkersInView(map, selectedCategory);
          }
        }, 300);
      };

      // 줌 레벨 변경 이벤트 (실제 데이터 로드는 이벤트 발생 시점에 수행)
      const handleZoomChanged = () => {
        // 컴포넌트가 언마운트된 경우 처리 중단
        if (!mountedRef.current) {
          console.log("언마운트 상태: 줌 변경 이벤트 무시");
          return;
        }

        // 현재 줌 레벨 가져오기
        const newZoom = map.getZoom();

        // 줌 레벨이 13, 10, 8일 때 마커를 리셋하고 다시 로드
        if (newZoom === 13 || newZoom === 10 || newZoom === 8) {
          console.log(`줌 레벨 ${newZoom} 도달: 모든 마커 리셋`);
          clearAllMarkers(); // 모든 마커 제거

          // 컴포넌트가 여전히 마운트 상태인지 확인
          if (mountedRef.current) {
            setPetLocations([]); // 위치 데이터 초기화

            // 약간의 지연 후 마커 다시 로드
            setTimeout(() => {
              // 타임아웃 실행 시점에도 마운트 상태 재확인
              if (mountedRef.current && !processingRef.current) {
                loadMarkersInView(map, selectedCategory);
              }
            }, 100);
          }
        } else {
          // 일반적인 경우 bounds_changed와 같은 로직 수행
          handleBoundsChanged();
        }

        // 현재 줌 레벨 업데이트
        if (mountedRef.current) {
          setCurrentZoom(newZoom);

          // 중심 좌표 가져와서 줌 레벨에 맞게 주소 업데이트
          const center = map.getCenter();
          if (center) {
            getAddressFromCoords(center.lat(), center.lng(), newZoom);
          }
        }
      };

      // 지도 중심 변경 이벤트 핸들러 (주소 업데이트)
      const handleCenterChanged = () => {
        if (!map || !mountedRef.current) return;

        const center = map.getCenter();
        const currentCenter = {
          lat: center.lat(),
          lng: center.lng(),
        };

        // 현재 위치와 지도 중심점의 차이가 0.0001 이내인 경우 (약 11m 이내)
        const isNearCurrentLocation =
          Math.abs(currentCenter.lat - currentLocation.lat) < 0.0001 &&
          Math.abs(currentCenter.lng - currentLocation.lng) < 0.0001;

        setIsAtCurrentLocation(isNearCurrentLocation);

        // 컴포넌트가 언마운트된 경우 처리 중단
        if (!mountedRef.current) {
          console.log("언마운트 상태: 중심 변경 이벤트 무시");
          return;
        }

        // 스로틀링: 마지막 호출 후 일정 시간이 지나야 실행
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
        }

        // 중심 좌표 업데이트 지연 적용 (드래그 중에는 업데이트 안함)
        loadTimeoutRef.current = setTimeout(() => {
          if (!mountedRef.current) return;

          // 중심 좌표와 줌 레벨 가져오기
          const center = map.getCenter();
          const zoom = map.getZoom();

          if (center) {
            // 좌표로부터 주소 가져오기
            getAddressFromCoords(center.lat(), center.lng(), zoom);
          }
        }, 300); // 300ms로 단축
      };

      // 드래그 종료 이벤트 핸들러 (주소 즉시 업데이트)
      const handleDragEnd = () => {
        // 컴포넌트가 언마운트된 경우 처리 중단
        if (!mountedRef.current) return;

        // 즉시 주소 업데이트 (지연 없음)
        const center = map.getCenter();
        const zoom = map.getZoom();

        if (center) {
          console.log("드래그 종료: 주소 업데이트");
          getAddressFromCoords(center.lat(), center.lng(), zoom);
        }
      };

      // 지도 이벤트 리스너 추가
      const boundsChangedListener = window.naver.maps.Event.addListener(
        map,
        "bounds_changed",
        handleBoundsChanged
      );
      const zoomChangedListener = window.naver.maps.Event.addListener(
        map,
        "zoom_changed",
        handleZoomChanged
      );
      const centerChangedListener = window.naver.maps.Event.addListener(
        map,
        "center_changed",
        handleCenterChanged
      );
      const dragEndListener = window.naver.maps.Event.addListener(
        map,
        "dragend",
        handleDragEnd
      );

      // 초기 마커 로드
      if (mountedRef.current) {
        console.log("초기 마커 로드 시작");
        loadMarkersInView(map, selectedCategory);

        // 초기 중심 좌표의 주소 가져오기
        const center = map.getCenter();
        if (center) {
          getAddressFromCoords(center.lat(), center.lng(), map.getZoom());
        }
      }

      return () => {
        // 컴포넌트 언마운트 시 이벤트 리스너 제거
        window.naver.maps.Event.removeListener(boundsChangedListener);
        window.naver.maps.Event.removeListener(zoomChangedListener);
        window.naver.maps.Event.removeListener(centerChangedListener);
        window.naver.maps.Event.removeListener(dragEndListener);

        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current);
          loadTimeoutRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded, map, isLoading, selectedCategory]);

  // map 상태 변경 감지
  useEffect(() => {
    console.log("map 상태 변경됨:", !!map);
  }, [map]);

  // 줌 레벨에 따른 표시할 마커 총 개수 제한 가져오기
  const getTotalMarkerLimit = (zoom: number): number => {
    if (zoom >= 15) return TOTAL_MARKERS_BY_ZOOM.ZOOM_15_PLUS;
    if (zoom === 14) return TOTAL_MARKERS_BY_ZOOM.ZOOM_14;
    if (zoom === 13) return TOTAL_MARKERS_BY_ZOOM.ZOOM_13;
    if (zoom === 12) return TOTAL_MARKERS_BY_ZOOM.ZOOM_12;
    if (zoom === 11) return TOTAL_MARKERS_BY_ZOOM.ZOOM_11;
    return TOTAL_MARKERS_BY_ZOOM.ZOOM_10; // 줌 레벨 10 이하
  };

  // 카테고리 스크롤 핸들러 함수 추가
  const handleCategoryScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (categoryScrollRef.current) {
      const scrollAmount = event.deltaY * 2; // 스크롤 속도 조정
      categoryScrollRef.current.scrollLeft += scrollAmount;
    }
  };

  // 핀 클릭 시 상세 정보 탭
  const handleStoreSelect = async (store: PetLocation) => {
    setSelectedStore(store);
    setShowStoreDetail(true);
    setStoreDetailInfo(null); // 이전 상세 정보 초기화
    setContentVisible(false); // 콘텐츠 초기 상태: 닫힘

    // 마커 선택 시 바텀시트를 무조건 partial로 설정 (포커싱된 마커가 있을 때는 1단계 높이 유지)
    setBottomSheetHeight("partial");

    // 이미 계산된 거리 정보가 있으면 사용, 없으면 새로 계산
    if (store.distance) {
      setStoreDistance(store.distance);
    } else {
      const distance = calculateDistanceFromCurrentLocation(
        store.lat,
        store.lng,
        currentLocation.lat,
        currentLocation.lng
      );
      setStoreDistance(distance);

      // 나중에 재사용할 수 있도록 저장
      store.distance = distance;
    }

    // JSON 데이터에서 상세 정보 가져오기 (Firestore 대신)
    const detailInfo = await fetchStoreDetailFromJSON(store.id, store.type);
    if (detailInfo) {
      setStoreDetailInfo(detailInfo);

      // 데이터 로드 완료 후 콘텐츠 표시 애니메이션 시작
      setTimeout(() => {
        if (mountedRef.current) {
          setContentVisible(true);
        }
      }, 100);
    } else {
      console.warn(`상세 정보를 가져올 수 없음: ${store.id}`);

      // 데이터가 없어도 UI는 표시
      setTimeout(() => {
        if (mountedRef.current) {
          setContentVisible(true);
        }
      }, 100);
    }
  };

  // 상세 정보 닫기 핸들러
  const handleCloseStoreDetail = () => {
    // 먼저 콘텐츠를 숨기고
    setContentVisible(false);

    // 마커 포커싱 상태 즉시 해제 (바텀시트 애니메이션과 동시에)
    setShowStoreDetail(false);
    setSelectedStore(null);
    setStoreDetailInfo(null);
    setStoreDistance("");

    // 마커 포커싱 해제 시 바텀시트를 0단계(collapsed)로 내리기
    setBottomSheetHeight("collapsed");
  };

  // selectedStore가 변경될 때 해당 마커만 크기를 키우는 효과 적용
  useEffect(() => {
    // map이 없거나 마커 참조가 없으면 실행하지 않음
    if (!map || !markersRef.current) return;

    // 네이버 마커 객체를 위한 타입 가드
    const isValidMarker = (marker: any): boolean => {
      return marker && typeof marker.getElement === "function";
    };

    // 모든 마커들을 순회하며 선택 상태에 따라 아이콘 업데이트
    Object.entries(markersRef.current).forEach(([markerId, marker]) => {
      if (!isValidMarker(marker)) return;

      try {
        // 해당 위치 데이터 찾기
        const location = petLocations.find((loc) => loc.id === markerId);
        if (!location) return;

        // 선택된 마커인지 확인
        const isSelected =
          selectedStore !== null && selectedStore.id === markerId;

        // 지역 정보 가져오기
        const region = getRegionFromCoords(location.lat, location.lng);

        // 새로운 마커 아이콘 생성
        const newMarkerIcon = getMarkerStyle(location, map, region, isSelected);

        // 마커 아이콘 업데이트
        marker.setIcon(newMarkerIcon);
      } catch (err) {
        console.error(`마커 아이콘 업데이트 중 오류 (ID: ${markerId}):`, err);
      }
    });
  }, [selectedStore, map, petLocations]);

  // 다른 마커가 선택되었을 때 이전 마커의 스타일 초기화
  useEffect(() => {
    const handleCloseStoreDetail = () => {
      // selectedStore가 변경될 때마다 기존 마커들 초기화
      Object.values(markersRef.current).forEach((marker) => {
        try {
          if (!marker || typeof marker.getElement !== "function") return;

          const markerElement = marker.getElement();
          if (!markerElement) return;

          const petMarker =
            markerElement.querySelector(".pet-location-marker") ||
            markerElement.querySelector(".pet-location-marker-small");

          if (petMarker) {
            petMarker.style.transform = "";
            petMarker.style.border = "";
            petMarker.style.zIndex = "";
            petMarker.style.boxShadow = "";
          }
        } catch (err) {
          // 오류 무시 - 일부 마커는 이미 제거되었을 수 있음
        }
      });
    };

    // selectedStore가 null이 되면 모든 마커 초기화
    if (!selectedStore) {
      handleCloseStoreDetail();
    }

    return () => {
      // selectedStore가 변경되면 기존에 선택된 마커 초기화
      handleCloseStoreDetail();
    };
  }, [selectedStore]);

  // 현재 지도 중심 주소 정보 상태 추가
  const [currentMapAddress, setCurrentMapAddress] = useState<string>("");
  // 마지막으로 주소 조회한 좌표 (불필요한 API 호출 방지)
  const lastGeocodedCenterRef = useRef<{ lat: number; lng: number } | null>(
    null
  );
  // 주소 업데이트 애니메이션을 위한 상태
  const [addressUpdateAnim, setAddressUpdateAnim] = useState<boolean>(false);

  // 좌표를 주소로 변환하는 함수 (네이버 지도 API의 reverseGeocode 활용)
  const getAddressFromCoords = (
    lat: number,
    lng: number,
    zoomLevel: number
  ) => {
    if (!window.naver || !window.naver.maps) {
      console.warn("네이버 지도 API가 로드되지 않았습니다.");
      return;
    }

    // 지오코딩 서비스 사용 가능 여부 확인
    if (!window.naver.maps.Service) {
      console.warn(
        "네이버 지도 지오코딩 서비스를 사용할 수 없습니다. 'submodules=geocoder' 옵션을 확인해주세요."
      );
      return;
    }

    // 이전 조회 좌표와 현재 좌표의 거리가 작으면 API 호출 스킵 (성능 최적화)
    if (lastGeocodedCenterRef.current) {
      const dist = getDistanceFromLatLonInKm(
        lat,
        lng,
        lastGeocodedCenterRef.current.lat,
        lastGeocodedCenterRef.current.lng
      );
      // 50m 내외면 주소 조회 스킵
      if (dist < 0.05) {
        return;
      }
    }

    // 현재 좌표 저장
    lastGeocodedCenterRef.current = { lat, lng };

    // 네이버 지도 API를 사용하여 좌표를 주소로 변환
    window.naver.maps.Service.reverseGeocode(
      {
        coords: new window.naver.maps.LatLng(lat, lng),
      },
      function (status: any, response: any) {
        if (status !== window.naver.maps.Service.Status.OK) {
          console.error("주소 변환 실패:", status);
          return;
        }

        try {
          const result = response.v2;
          if (!result || !result.results || result.results.length === 0) {
            console.warn("주소 정보가 없습니다.");
            return;
          }

          // 지역 정보 추출
          const region = result.results[0].region;

          // 줌 레벨에 따라 주소 상세도 조절
          let formattedAddress = "";

          if (zoomLevel <= 9) {
            // 줌 레벨이 낮을 때는 시/도만 표시
            formattedAddress = region.area1.name;
          } else if (zoomLevel <= 12) {
            // 중간 줌 레벨에서는 시/군/구까지 표시
            formattedAddress = `${region.area1.name} ${region.area2.name}`;
          } else if (zoomLevel <= 14) {
            // 높은 줌 레벨에서는 읍/면/동까지 표시
            formattedAddress = `${region.area1.name} ${region.area2.name} ${region.area3.name}`;
          } else {
            // 아주 높은 줌 레벨에서는 상세 주소 표시
            formattedAddress =
              result.address.roadAddress ||
              result.address.jibunAddress ||
              `${region.area1.name} ${region.area2.name} ${region.area3.name}`;
          }

          console.log("주소 변환 결과:", formattedAddress);

          // 주소가 변경된 경우만 업데이트 및 애니메이션 적용
          if (formattedAddress !== currentMapAddress) {
            // 주소 상태 업데이트
            setCurrentMapAddress(formattedAddress);

            // 애니메이션 효과 적용
            setAddressUpdateAnim(true);

            // 애니메이션 효과 제거 (300ms 후)
            setTimeout(() => {
              if (mountedRef.current) {
                setAddressUpdateAnim(false);
              }
            }, 300);
          }
        } catch (error) {
          console.error("주소 처리 중 오류:", error);
        }
      }
    );
  };

  // 바텀시트 상태 관리
  const [bottomSheetHeight, setBottomSheetHeight] = useState<
    "collapsed" | "partial" | "expanded"
  >("partial");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartY, setDragStartY] = useState<number>(0);
  const [dragCurrentY, setDragCurrentY] = useState<number>(0);
  const [dragStartHeight, setDragStartHeight] = useState<
    "collapsed" | "partial" | "expanded"
  >("partial");

  // 바텀시트 드래그 관련 ref
  const bottomSheetRef = useRef<HTMLDivElement>(null);

  // 바텀시트 높이 설정
  const getBottomSheetStyle = () => {
    if (isDragging) {
      // 드래그 중일 때는 실시간으로 높이 조절
      const deltaY = dragStartY - dragCurrentY;
      let baseHeight = 0; // collapsed 기본 높이

      if (dragStartHeight === "partial") {
        baseHeight = 192; // window.innerHeight * 0.5;
      } else if (dragStartHeight === "expanded") {
        baseHeight = window.innerHeight * 0.75;
      }

      // 최소/최대 높이 제한
      const minHeight = 0;
      const maxHeight = window.innerHeight * 0.75; // 최대 85%까지 확장 가능
      const newHeight = Math.max(
        minHeight,
        Math.min(maxHeight, baseHeight + deltaY)
      );

      return {
        height: `${newHeight}px`,
        transition: "none", // 드래그 중에는 트랜지션 제거
        transform: "translateY(0)",
      };
    }

    // 드래그가 아닐 때는 상태에 따른 고정 높이
    switch (bottomSheetHeight) {
      case "collapsed":
        return {
          height: "70px",
          transition: "height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
        };
      case "partial":
        return {
          height: "192px",
          transition: "height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
        };
      case "expanded":
        return {
          height: "75vh",
          transition: "height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
        };
      default:
        return {
          height: "0",
          transition: "height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
        };
    }
  };

  // 바텀시트 드래그 시작
  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsDragging(true);
    setDragStartHeight(bottomSheetHeight);

    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setDragStartY(clientY);
    setDragCurrentY(clientY);

    // 드래그 중 선택 방지
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
  };

  // 바텀시트 드래그 중
  const handleDragMove = (e: TouchEvent | MouseEvent) => {
    if (!isDragging) return;

    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    setDragCurrentY(clientY);
  };

  // 바텀시트 드래그 종료
  const handleDragEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    document.body.style.userSelect = "";
    document.body.style.webkitUserSelect = "";

    // 현재 드래그된 높이 계산
    const deltaY = dragStartY - dragCurrentY;
    let baseHeight = 0; // collapsed 기본 높이 (사용자 수정값 반영)

    if (dragStartHeight === "partial") {
      baseHeight = 192; // 사용자 수정값 반영
    } else if (dragStartHeight === "expanded") {
      baseHeight = window.innerHeight * 0.75;
    }

    const currentHeight = Math.max(
      0,
      Math.min(window.innerHeight * 0.75, baseHeight + deltaY)
    );

    // 각 상태의 높이값 정의 (사용자 수정값 반영)
    const collapsedHeight = 70; // 사용자 수정값
    const partialHeight = 192; // 사용자 수정값
    const expandedHeight = window.innerHeight * 0.75;

    // 현재 높이와 각 상태 높이의 차이 계산
    const distanceToCollapsed = Math.abs(currentHeight - collapsedHeight);
    const distanceToPartial = Math.abs(currentHeight - partialHeight);
    const distanceToExpanded = Math.abs(currentHeight - expandedHeight);

    // 가장 가까운 상태 찾기 (포커싱된 마커가 있을 때는 expanded 제외)
    let minDistance;
    if (showStoreDetail || selectedStore) {
      // 포커싱된 마커가 있을 때는 collapsed와 partial 중에서만 선택
      minDistance = Math.min(distanceToCollapsed, distanceToPartial);
    } else {
      // 포커싱된 마커가 없을 때는 모든 상태 고려
      minDistance = Math.min(
        distanceToCollapsed,
        distanceToPartial,
        distanceToExpanded
      );
    }

    let newState: "collapsed" | "partial" | "expanded" = "collapsed";

    if (minDistance === distanceToCollapsed) {
      newState = "collapsed";
    } else if (minDistance === distanceToPartial) {
      newState = "partial";
    } else if (
      minDistance === distanceToExpanded &&
      !(showStoreDetail || selectedStore)
    ) {
      // expanded는 포커싱된 마커가 없을 때만 허용
      newState = "expanded";
    }

    // 최소 드래그 거리 체크 (너무 작은 움직임은 무시)
    const minDragDistance = 30;
    if (Math.abs(deltaY) < minDragDistance) {
      // 드래그 거리가 너무 작으면 원래 상태 유지
      newState = dragStartHeight as "collapsed" | "partial" | "expanded";
    }

    // 드래그 방향 고려한 추가 로직
    if (Math.abs(deltaY) >= minDragDistance) {
      // 위로 드래그한 경우 (deltaY > 0)
      if (deltaY > 0) {
        // 포커싱된 마커가 있는 경우 partial을 최대 높이로 제한
        if (showStoreDetail || selectedStore) {
          // 현재 높이가 중간값보다 높으면 partial로 설정
          if (currentHeight > (collapsedHeight + partialHeight) / 2) {
            newState = "partial";
          }
        } else {
          // 포커싱된 마커가 없을 때는 기존 로직 적용
          if (
            currentHeight > (collapsedHeight + partialHeight) / 2 &&
            currentHeight < (partialHeight + expandedHeight) / 2
          ) {
            newState = "partial";
          } else if (currentHeight >= (partialHeight + expandedHeight) / 2) {
            newState = "expanded";
          }
        }
      }
      // 아래로 드래그한 경우 (deltaY < 0)
      else {
        // 현재 높이가 중간값들 사이에 있을 때 더 낮은 상태로
        if (
          currentHeight < (partialHeight + expandedHeight) / 2 &&
          currentHeight > (collapsedHeight + partialHeight) / 2
        ) {
          newState = "partial";
        } else if (currentHeight <= (collapsedHeight + partialHeight) / 2) {
          newState = "collapsed";
        }
      }
    }

    // 포커싱된 마커가 있을 때 expanded 상태 방지
    if ((showStoreDetail || selectedStore) && newState === "expanded") {
      newState = "partial";
      console.log("포커싱된 마커가 있어 expanded → partial로 변경");
    }

    console.log(
      `드래그 종료: 현재 높이 ${currentHeight}px, 새 상태: ${newState}`
    );

    // 바텀시트 상태 설정
    setBottomSheetHeight(newState);

    // collapsed 상태로 변경되고 현재 포커싱된 마커가 있다면 해제
    if (newState === "collapsed" && (showStoreDetail || selectedStore)) {
      console.log("드래그로 collapsed: 포커싱된 마커 해제");

      // 콘텐츠 숨김 애니메이션
      setContentVisible(false);

      // 마커 포커싱 상태 해제
      setTimeout(() => {
        if (mountedRef.current) {
          setShowStoreDetail(false);
          setSelectedStore(null);
          setStoreDetailInfo(null);
          setStoreDistance("");
        }
      }, 200);
    }
  };

  // 드래그 이벤트 리스너 등록
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => handleDragMove(e);
      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault(); // 스크롤 방지
        handleDragMove(e);
      };
      const handleMouseUp = () => handleDragEnd();
      const handleTouchEnd = () => handleDragEnd();

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, dragStartY, dragCurrentY, dragStartHeight]);

  // 현재 지도 영역의 마커 리스트 가져오기 (무한 스크롤용)
  const [displayedMarkerCount, setDisplayedMarkerCount] = useState<number>(10); // 초기 개수를 10개로 줄임
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

  const getVisibleMarkers = React.useMemo(() => {
    if (!map) return [];

    const bounds = map.getBounds();
    if (!bounds) return [];

    // 선택된 카테고리에 따라 필터링
    const filteredLocations = filterLocationsByCategory(
      selectedCategory,
      petLocations
    );

    const visibleMarkers = filteredLocations.filter((location) =>
      isLocationInBounds(location.lat, location.lng, bounds)
    );

    // 거리 순으로 정렬
    const sortedMarkers = visibleMarkers.sort((a, b) => {
      const distanceA = a.distance
        ? parseFloat(a.distance.replace(/[^0-9.]/g, ""))
        : 0;
      const distanceB = b.distance
        ? parseFloat(b.distance.replace(/[^0-9.]/g, ""))
        : 0;
      return distanceA - distanceB;
    });

    return sortedMarkers.slice(0, displayedMarkerCount);
  }, [map, selectedCategory, petLocations, displayedMarkerCount]);

  // 더 많은 마커 로드 함수
  const loadMoreMarkers = React.useCallback(() => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);

    // 로딩 시간을 200ms로 줄여서 더 빠른 응답성 제공
    setTimeout(() => {
      setDisplayedMarkerCount((prev) => prev + 10); // 한 번에 10개씩만 추가
      setIsLoadingMore(false);
    }, 200);
  }, [isLoadingMore]);

  // 스크롤 스로틀링을 위한 ref
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 스크롤 이벤트 핸들러 (스로틀링 적용)
  const handleMarkerListScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      try {
        // 이벤트 객체와 currentTarget의 유효성 체크
        if (!e || !e.currentTarget) {
          console.warn("스크롤 이벤트 또는 currentTarget이 null입니다.");
          return;
        }

        // 기존 타이머가 있으면 제거
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // currentTarget을 변수로 저장 (클로저 문제 방지)
        const targetElement = e.currentTarget;

        // 100ms 후에 스크롤 처리 실행 (스로틀링)
        scrollTimeoutRef.current = setTimeout(() => {
          try {
            // 저장된 element가 여전히 유효한지 체크
            if (!targetElement || targetElement.scrollTop === undefined) {
              console.warn(
                "스크롤 타이머 실행 시 targetElement가 유효하지 않습니다."
              );
              return;
            }

            const { scrollTop, scrollHeight, clientHeight } = targetElement;

            // 스크롤이 거의 끝에 도달했을 때 더 로드
            if (
              scrollHeight - scrollTop <= clientHeight + 100 &&
              !isLoadingMore
            ) {
              const totalVisibleMarkers = (() => {
                if (!map) return 0;
                const bounds = map.getBounds();
                if (!bounds) return 0;

                const filteredLocations = filterLocationsByCategory(
                  selectedCategory,
                  petLocations
                );

                return filteredLocations.filter((location) =>
                  isLocationInBounds(location.lat, location.lng, bounds)
                ).length;
              })();

              // 아직 더 로드할 데이터가 있는 경우에만 로드
              if (displayedMarkerCount < totalVisibleMarkers) {
                loadMoreMarkers();
              }
            }
          } catch (timeoutError) {
            console.error("스크롤 타이머 실행 중 오류:", timeoutError);
          }
        }, 100);
      } catch (error) {
        console.error("스크롤 이벤트 처리 중 오류:", error);
      }
    },
    [
      map,
      selectedCategory,
      petLocations,
      displayedMarkerCount,
      isLoadingMore,
      loadMoreMarkers,
    ]
  );

  // 카테고리나 지도 영역이 변경될 때 표시 개수 초기화
  useEffect(() => {
    setDisplayedMarkerCount(10);
  }, [selectedCategory, currentZoom]);

  // 마커 리스트 아이템 클릭 핸들러
  const handleMarkerListItemClick = React.useCallback(
    (location: PetLocation) => {
      // 해당 마커로 지도 이동
      if (map) {
        const center = new window.naver.maps.LatLng(location.lat, location.lng);
        map.morph(center, 16);
      }

      // 매장 상세 정보 표시
      handleStoreSelect(location);

      // 바텀시트 상태는 현재 상태 유지 (collapsed로 변경하지 않음)
      // setBottomSheetHeight('collapsed'); // 이 줄 제거
    },
    [map, handleStoreSelect]
  );

  // 내 위치 버튼 클릭 핸들러
  const handleMyLocationClick = () => {
    if (!map) return;

    // 위치 권한 확인 및 현재 위치 가져오기
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        // 지도를 현재 GPS 위치로 이동
        const center = new window.naver.maps.LatLng(latitude, longitude);
        map.morph(center, 16);

        // 현재 위치 상태 업데이트
        setCurrentLocation({
          lat: latitude,
          lng: longitude,
        });

        // 현재 위치 표시 활성화
        setIsAtCurrentLocation(true);

        // 주소 정보 업데이트
        getAddressFromCoords(latitude, longitude, 16);

        // 마커 다시 로드
        loadMarkersInView(map, selectedCategory);
      },
      (error) => {
        console.error("위치 정보를 가져올 수 없습니다:", error);
        alert("위치 정보를 가져올 수 없습니다. 위치 권한을 확인해주세요.");
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  };

  // JSON 데이터에서 상세 정보를 가져오는 함수 (Firestore 대신 사용)
  const fetchStoreDetailFromJSON = async (
    storeId: string,
    storeType: string
  ) => {
    try {
      setIsLoadingDetail(true);

      console.log(
        `JSON 데이터에서 상세 정보 가져오기: ${storeType}/${storeId}`
      );

      // 스토어 타입에 따라 해당하는 JSON 데이터에서 찾기
      let targetData: any[] = [];

      switch (storeType) {
        case "동물병원":
          targetData = animalHospitalsData as any[];
          break;
        case "카페":
          targetData = cafesData as any[];
          break;
        case "애견카페":
          targetData = petCafesData as any[];
          break;
        case "애견공원":
          targetData = petParksData as any[];
          break;
        case "장례식장":
          targetData = petFuneralHomesData as any[];
          break;
        case "식당":
          targetData = restaurantsData as any[];
          break;
        default:
          console.warn(`지원하지 않는 매장 타입: ${storeType}`);
          return null;
      }

      // 해당 ID의 데이터 찾기
      const foundStore = targetData.find((item: any) => item.id === storeId);

      if (foundStore) {
        // JSON 데이터를 StoreDetailInfo 형식으로 변환
        const storeInfo: StoreDetailInfo = {
          id: foundStore.id || storeId,
          name: foundStore.name || "",
          address: foundStore.address || "",
          roadAddress: foundStore.address || "", // roadAddress가 없으면 address 사용
          x: foundStore.x ? parseFloat(foundStore.x) : undefined,
          y: foundStore.y ? parseFloat(foundStore.y) : undefined,
          // JSON 데이터에 없는 필드들은 기본값으로 설정
          category: storeType,
          phone: undefined,
          detailInfo: undefined,
          hasImage: !!foundStore.thumbnail,
          imageUrls: foundStore.thumbnail ? [foundStore.thumbnail] : [],
          sites: [],
          hasWebsite: false,
          markerDisplayInfo: undefined,
          location: undefined,
          geohash: undefined,
          g: undefined,
          updatedAt: undefined,
        };

        console.log(`상세 정보 로드 성공:`, storeInfo);
        return storeInfo;
      } else {
        console.log(`데이터를 찾을 수 없음: ${storeType}/${storeId}`);
        return null;
      }
    } catch (error) {
      console.error(`JSON 상세 정보 로드 오류:`, error);
      return null;
    } finally {
      // 로딩 완료 후 애니메이션을 위해 약간의 지연 추가
      setTimeout(() => {
        if (mountedRef.current) {
          setIsLoadingDetail(false);
        }
      }, 100);
    }
  };

  // 성능 최적화를 위한 메모화된 스토어 리스트 아이템 컴포넌트
  const StoreListItem = React.memo(
    ({
      location,
      onItemClick,
      onDirectionClick,
      styles,
    }: {
      location: PetLocation;
      onItemClick: (location: PetLocation) => void;
      onDirectionClick: (location: PetLocation) => void;
      styles: any;
    }) => {
      return (
        <div
          className={styles["store-info-content"]}
          onClick={() => onItemClick(location)}
        >
          {/* 매장 기본 정보 */}
          <div className={styles["store-basic-info"]}>
            <h2>{location.name}</h2>
            <div className={styles["store-meta"]}>
              <span className={styles["store-category"]}>{location.type}</span>
            </div>
            <div className={styles["store-address-info"]}>
              {location.distance && (
                <span className={styles["store-distance"]}>
                  {location.distance}
                </span>
              )}
              <span>∙</span>
              <span>{location.address || "주소 정보 없음"}</span>
            </div>
            <div
              className={styles["store-direction-button"]}
              onClick={(e) => {
                e.stopPropagation();
                handleDirectionClick(location);
              }}
            >
              <img src="/images/icons/map-direction.svg" alt="길찾기" />
              <span>길찾기</span>
            </div>
          </div>
          <div className={styles["store-image-section"]}>
            <div className={styles["store-image-placeholder"]}>
              <img
                src={location.thumbnail || "/images/icons/no-image.png"}
                alt={location.name || "매장 이미지"}
                onError={(e) => {
                  // 이미지 로드 실패 시 대체 이미지 표시
                  const target = e.target as HTMLImageElement;
                  target.onerror = null; // 무한 루프 방지
                  target.src = "/images/icons/no-image.png";
                }}
              />
            </div>
          </div>
        </div>
      );
    }
  );

  // 모바일 기기 감지 함수
  const isMobile = (): boolean => {
    // User Agent 기반 모바일 감지
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = [
      "mobile",
      "android",
      "iphone",
      "ipad",
      "ipod",
      "blackberry",
      "windows phone",
      "opera mini",
    ];

    // User Agent에 모바일 키워드가 포함되어 있는지 확인
    const isMobileUA = mobileKeywords.some((keyword) =>
      userAgent.includes(keyword)
    );

    // 화면 크기 기반 감지 (768px 이하를 모바일로 간주)
    const isMobileScreen = window.innerWidth <= 768;

    // 터치 이벤트 지원 여부 확인
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // 모바일 조건 중 하나라도 만족하면 모바일로 간주
    return isMobileUA || (isMobileScreen && isTouchDevice);
  };

  // 네이버 지도 길찾기 링크 생성 함수
  const generateNaverDirectionLink = (
    endLat: number,
    endLng: number,
    endName: string = "목적지",
    startLat?: number,
    startLng?: number,
    startName?: string
  ): string => {
    // 모바일 여부 확인
    const mobile = isMobile();

    // if (mobile) {
    //   // 모바일인 경우 nmap 앱 링크 사용
    //   let nmapUrl = `nmap://route/public?dlat=${endLat}&dlng=${endLng}&dname=${encodeURIComponent(
    //     endName
    //   )}`;

    //   // 현재 위치 정보가 있는 경우 출발지 추가
    //   if (startLat !== undefined && startLng !== undefined) {
    //     nmapUrl += `&slat=${startLat}&slng=${startLng}&sname=${encodeURIComponent(
    //       startName || "현재 위치"
    //     )}`;
    //   }

    //   return nmapUrl;
    // } else {
    // }

    // PC인 경우 웹 브라우저 네이버 지도 사용
    const baseUrl = "http://m.map.naver.com/route.nhn";
    const params = new URLSearchParams({
      menu: "route",
      ename: endName,
      ex: endLng.toString(),
      ey: endLat.toString(),
      pathType: "0", // 0: 빠른길, 1: 무료도로, 2: 최단거리
      showMap: "true",
    });

    // 현재 위치 정보가 있는 경우에만 출발지 설정
    if (startLat !== undefined && startLng !== undefined) {
      params.set("sname", startName || "현재 위치");
      params.set("sx", startLng.toString());
      params.set("sy", startLat.toString());
    }

    return `${baseUrl}?${params.toString()}`;
  };

  // 길찾기 버튼 클릭 핸들러
  const handleDirectionClick = (targetLocation: PetLocation) => {
    try {
      console.log("길찾기 요청:", {
        target: targetLocation,
        currentLocation: currentLocation,
        locationLoaded: locationLoaded,
      });

      // 현재 위치 정보가 로드되었고 유효한 경우
      let startLat: number | undefined;
      let startLng: number | undefined;
      let startName: string | undefined;

      if (
        locationLoaded &&
        currentLocation.lat !== 37.5665 &&
        currentLocation.lng !== 126.978
      ) {
        // 기본 서울 좌표가 아닌 실제 현재 위치인 경우
        startLat = currentLocation.lat;
        startLng = currentLocation.lng;
        startName = "현재 위치";
      }

      const directionUrl = generateNaverDirectionLink(
        targetLocation.lat,
        targetLocation.lng,
        targetLocation.name,
        startLat,
        startLng,
        startName
      );

      console.log("네이버 길찾기 링크:", directionUrl);

      // 새 창에서 네이버 지도 길찾기 열기
      window.open(directionUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("길찾기 링크 생성 중 오류:", error);
      alert("길찾기 서비스를 열 수 없습니다. 다시 시도해주세요.");
    }
  };

  // selectedCategory 상태 변화 추적 (디버깅용)
  useEffect(() => {
    console.log(`📊 selectedCategory 상태 변경됨: ${selectedCategory}`);
  }, [selectedCategory]);

  // 검색 데이터 처리 함수
  const handleSearchData = React.useCallback(
    (searchLocation: any, selectedStore: any) => {
      if (!map) return;

      console.log("검색 데이터 처리:", { searchLocation, selectedStore });

      // 검색된 위치로 지도 이동
      if (searchLocation) {
        console.log("검색 결과로 지도 이동:", searchLocation);

        // 지도 중심 이동
        const center = new window.naver.maps.LatLng(
          searchLocation.lat,
          searchLocation.lng
        );
        map.morph(center, 16);

        // 주소 정보 업데이트
        setTimeout(() => {
          if (mountedRef.current) {
            getAddressFromCoords(searchLocation.lat, searchLocation.lng, 16);
          }
        }, 500);

        // 새로운 위치의 마커들 로드
        setTimeout(() => {
          if (mountedRef.current) {
            loadMarkersInView(map, selectedCategory);
          }
        }, 600);
      }

      // 선택된 매장이 있으면 해당 매장 선택 (지도 이동 후 약간의 지연)
      if (selectedStore) {
        console.log("검색으로 선택된 매장:", selectedStore);

        setTimeout(() => {
          if (mountedRef.current) {
            // handleStoreSelect 대신 직접 상태 설정
            setSelectedStore(selectedStore);
            setShowStoreDetail(true);
            setStoreDetailInfo(null);
            setContentVisible(false);
            setBottomSheetHeight("partial");

            // 거리 계산
            const distance = calculateDistanceFromCurrentLocation(
              selectedStore.lat,
              selectedStore.lng,
              currentLocation.lat,
              currentLocation.lng
            );
            setStoreDistance(distance);
            selectedStore.distance = distance;

            // 상세 정보 로드
            fetchStoreDetailFromJSON(selectedStore.id, selectedStore.type).then(
              (detailInfo) => {
                if (detailInfo && mountedRef.current) {
                  setStoreDetailInfo(detailInfo);
                  setTimeout(() => {
                    if (mountedRef.current) {
                      setContentVisible(true);
                    }
                  }, 100);
                }
              }
            );
          }
        }, 800); // 지도 이동 애니메이션 후 매장 선택
      }
    },
    [map, currentLocation.lat, currentLocation.lng]
  );

  // 검색 페이지에서 전달받은 데이터 처리
  useEffect(() => {
    if (location.state && map) {
      const { searchLocation, selectedStore } = location.state as any;

      // 검색 데이터 처리
      handleSearchData(searchLocation, selectedStore);

      // 처리 후 state 초기화 (뒤로가기 시 재실행 방지)
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, map, navigate, handleSearchData]);

  // 검색 입력 클릭 핸들러
  const handleSearchInputClick = () => {
    navigate("/map/search");
  };

  const [isAtCurrentLocation, setIsAtCurrentLocation] = useState(false);

  return (
    <div className={styles["map-page"]}>
      <div className={styles["map-search-container"]}>
        <div
          className={styles["search-input-wrapper"]}
          onClick={handleSearchInputClick}
        >
          <img
            src="/images/icons/map-search.svg"
            alt="검색"
            className={styles["search-icon"]}
          />
          <input
            type="text"
            placeholder="지역 및 상호명을 검색해주세요"
            className={styles["map-search-input"]}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            readOnly
          />
        </div>

        {/* 카테고리 필터 추가 */}
        <div className={styles["Filter_pills"]}>
          <div
            className={styles["category-filter-scroll"]}
            ref={categoryScrollRef}
            onWheel={handleCategoryScroll}
          >
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "전체" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("전체")}
            >
              전체
            </button>
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "내 피드" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("내 피드")}
            >
              <img
                src="/images/icons/map-category-feed.svg"
                alt="내 피드"
                loading="lazy"
              />
              내 피드
            </button>
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "펫 카페/운동장" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("펫 카페/운동장")}
            >
              <img
                src="/images/icons/map-category-playground.svg"
                alt="펫 카페/운동장"
                loading="lazy"
              />
              펫 카페/ 운동장
            </button>
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "식당" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("식당")}
            >
              <img
                src="/images/icons/map-category-restaurant.svg"
                alt="식당"
                loading="lazy"
              />
              식당
            </button>
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "카페" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("카페")}
            >
              <img
                src="/images/icons/map-category-cafe.svg"
                alt="카페"
                loading="lazy"
              />
              카페
            </button>
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "병원" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("병원")}
            >
              <img
                src="/images/icons/map-category-hospital.svg"
                alt="병원"
                loading="lazy"
              />
              병원
            </button>
            <button
              className={`${styles["category-pill"]} ${
                selectedCategory === "장례식장" ? styles["active"] : ""
              }`}
              onClick={() => handleCategorySelect("장례식장")}
            >
              <img
                src="/images/icons/map-category-funeral.svg"
                alt="장례식장"
                loading="lazy"
              />
              장례식장
            </button>
          </div>
        </div>
      </div>

      <div className={styles["map-container"]} ref={mapRef}>
        {isLoading && (
          <div className={styles["map-loading-indicator"]}>
            <div className={styles["loading-spinner"]}></div>
          </div>
        )}
      </div>

      {/* 핀 클릭 시 상세 정보 탭 */}
      <div
        className={`${styles["store-detail-bottom-sheet"]} ${
          isDragging ? styles["dragging"] : ""
        }`}
        ref={bottomSheetRef}
        style={getBottomSheetStyle()}
      >
        {/* 내 위치 버튼 - expanded 상태가 아닐 때만 표시 */}
        {bottomSheetHeight !== "expanded" && (
          <div className={styles["bottom-sheet-my-location"]}>
            <div
              className={styles["my-location-button"]}
              onClick={handleMyLocationClick}
            >
              <img
                src={`/images/icons/${
                  isAtCurrentLocation
                    ? "map-my-location-active"
                    : "map-my-location"
                }.svg`}
                alt="위치"
              />
            </div>
          </div>
        )}
        {/* 상단 핸들바 */}
        <div
          className={styles["bottom-sheet-handle"]}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className={styles["handle-indicator"]}></div>
          <div className={styles["map-address-info"]}>
            <img src="/images/icons/profile-pin.svg" alt="위치" />
            <span>{currentMapAddress || "-"}</span>
          </div>
        </div>

        {/* 마커 리스트 (partial/expanded 상태에서만 표시하고, 포커싱된 마커가 없을 때만) */}
        {(bottomSheetHeight === "partial" ||
          bottomSheetHeight === "expanded") &&
          !showStoreDetail &&
          !selectedStore && (
            <div className={styles["marker-list-container"]}>
              <div
                className={styles["marker-list"]}
                onScroll={handleMarkerListScroll}
              >
                {getVisibleMarkers.map((location) => (
                  <StoreListItem
                    key={location.id}
                    location={location}
                    onItemClick={handleMarkerListItemClick}
                    onDirectionClick={handleDirectionClick}
                    styles={styles}
                  />
                ))}

                {/* 무한 스크롤 로딩 인디케이터 */}
                {isLoadingMore && (
                  <div className={styles["loading-more-indicator"]}>
                    <div className={styles["loading-spinner"]}></div>
                    <span>더 많은 매장을 불러오는 중...</span>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* 포커싱된 마커 정보 (showStoreDetail이 true이고 selectedStore가 있을 때만) */}
        {showStoreDetail && selectedStore && (
          <>
            {/* 상단 헤더 */}
            <div className={styles["bottom-sheet-header"]}>
              <button
                className={styles["close-button"]}
                onClick={handleCloseStoreDetail}
              >
                <img src="/images/icons/map-close.svg" alt="닫기" />
              </button>
            </div>

            {/* 매장 정보 */}
            <div
              className={`${styles["slide-up-container"]} ${
                contentVisible ? styles["active"] : ""
              }`}
            >
              <StoreListItem
                location={selectedStore}
                onItemClick={() => {}} // 상세 화면에서는 클릭 이벤트 불필요
                onDirectionClick={handleDirectionClick}
                styles={styles}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MapPage;
